import AdminLayout from "../../commponents/layout/adminLayout";
import { useEffect, useState } from "react";
import type { TableColumn } from "../../commponents/table/types";
import { Download, Eye, Pencil, Plus, Trash2 } from "lucide-react";

import {
  deleteInvoiceById,
  getInvoice,
  getInvoiceStatus,
  invoicePdf,
} from "../../api/invoiceApi";
import { useNavigate } from "react-router-dom";
import InvoicePdfPreviewModal from "../../commponents/invoice/InvoicePdfPreviewModal";
import DataTable from "../../commponents/table/DataTable";
import StatusFilter from "../../commponents/filter/StatusFilter";
import type { status } from "../../commponents/StatusDropdown";
import { showConfirm, showError, showSuccess } from "../../utils/ alert";

interface Invoice {
  id: number;
  invoice_no: string;
  recipient_name: string;
  invoice_date: string;
  grand_total: string;
  invoiceStatus: {
                    id: number,
                    name: string,
                    "code": string,
                    "color": string
                }
  createdAt: string;
  updatedAt: string;

  sales?: {
    id: number;
    name: string;
  };
}

export default function InvoiceList() {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [invoice, setInvoice] = useState<Invoice[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  // ---- State modal preview PDF ----
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewFilename, setPreviewFilename] = useState("invoice.pdf");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [statusOptions, setStatusOptions] = useState<status[]>([]);
  


  const navigate = useNavigate();

  const columns: TableColumn<Invoice>[] = [
    { key: "invoice_date", title: "Date" },
    { key: "recipient_name", title: "To" },
    { key: "invoice_no", title: "No" },
    {
      key: "id",
      title: "Sales",
      render: (row) => row.sales?.name ?? "-",
    },
    {
      key: "invoiceStatus",
      title: "Status",
      render: (row) => (
        <span
          className="font-medium"
          style={{
            color: row.invoiceStatus?.color ?? "#6b7280",
          }}>
          {row.invoiceStatus?.name ?? "-"}
        </span>
      ),
    },
    {
      key: "id",
      title: " ",
      render: (row) => (
        <div className="flex items-center gap-2">
          {(() => {
            switch (row.invoiceStatus.code) {
             

              case "OVERDUE":
                return (
                  <span className="text-sm font-medium text-red-600">
                    ⚠️ Invoice telah melewati due date selama 3 hari.
                  </span>
                );

              default:
                return null;
            }
          })()}
        </div>
      ),
    },
    {
      key: "id",
      title: "Action",
      render: (row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleDetail(row.id)}
            className="rounded-lg bg-blue-100 p-2 text-blue-600 hover:bg-blue-200">
            <Eye size={18} />
          </button>

          <button
            onClick={() => handlePreviewPdf(row)}
            className="rounded-lg bg-blue-100 p-2 text-blue-600 hover:bg-blue-200">
            <Download size={18} />
          </button>

          <button
            onClick={() => handleEdit(row.id)}
            className="rounded-lg bg-yellow-100 p-2 text-yellow-600 hover:bg-yellow-200">
            <Pencil size={18} />
          </button>

          <button
            onClick={() => handleDelete(row.id)}
            className="rounded-lg bg-red-100 p-2 text-red-600 hover:bg-red-200">
            <Trash2 size={18} />
          </button>
        </div>
      ),
    },
  ];




  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const response = await getInvoice({
        page: currentPage,
        limit: pageSize,
        search,
         status,
      });

      setInvoice(response.data.invoiceList);
      setTotalPages(response.data.pagination.totalPages);

      const dataStatus = await getInvoiceStatus()

      setStatusOptions(dataStatus.data.invoiceList);


    } catch (error) {
      showError(
        "Failed",
        "Failed to Load data."
    );
    } finally {
      setLoading(false);
    }
  };

  


  useEffect(() => {
    fetchInvoice();
  }, [currentPage, pageSize, search, status]);

  // Cleanup object URL kalau komponen unmount saat modal masih terbuka
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleDetail = (id: number) => {
    navigate(`/invoice/detail/${id}`);
  };

  const handleEdit = (id: number) => {
    navigate(`/invoice/edit/${id}`);
  };

  const handlePreviewPdf = async (row: Invoice) => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewUrl(null);
    setPreviewFilename(`${row.invoice_no || "invoice"}.pdf`);

    try {
      const response = await invoicePdf(row.id);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (error) {
      showError(
        "Failed",
        "Failed to Load data."
    );
      showError("Failed to load invoice preview.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
  };

  const handleDelete = async (id: number) => {
    try {
      const confirmed = await showConfirm(
        "Delete Invoice",
        "Are you sure you want to delete this invoice?"
      );
      if (!confirmed) return;

      await deleteInvoiceById(id);
      await fetchInvoice();

     await showSuccess("Deleted", "Invoice deleted successfully.");
    } catch (error) {
      showError(
        "Failed",
        "Failed to Load data."
    );
      showError("Failed to delete Invoice.");
    }
  };

  const handleAdd = () => navigate("/invoice/add");



  return (
    <AdminLayout>
      <div className="rounded-3xl bg-white p-6">
        <DataTable
          title="Invoice List"
          columns={columns}
          data={invoice}
          onAdd={handleAdd}
          filterContent={
            <>
              <StatusFilter
                value={status}
                onChange={setStatus}
                options={statusOptions.map((data) => {
                  return {
                    label: data.name,
                    value: data.code,
                  };
                })}
              />
            </>
          }
          onSearch={(value) => {
            // FIX: reset ke halaman 1 tiap kali keyword search berubah,
            // supaya tidak "nyangkut" di halaman kosong (mis. lagi di
            // halaman 3 lalu ngetik keyword baru yang hasilnya cuma 1 halaman).
            setSearch(value);
            setCurrentPage(1);
          }}
          pagination={{
            currentPage,
            totalPages,
            pageSize,
            onPageChange: setCurrentPage,
            onPageSizeChange: (size) => {
              setPageSize(size);
              setCurrentPage(1);
            },
          }}
        />

        <InvoicePdfPreviewModal
          open={previewOpen}
          loading={previewLoading}
          pdfUrl={previewUrl}
          filename={previewFilename}
          onClose={handleClosePreview}
        />
      </div>
    </AdminLayout>
  );
}
